import { formatChartTooltipTime } from '../../../utils/DateUtils';

// Настраиваемая подсказка для графика
export const createCustomTooltip = (formatTooltipLabel, reportType) => ({
  enabled: true,
  position: 'nearest',
  backgroundColor: 'rgba(0, 0, 0, 0.8)',
  titleFont: {
    size: 14,
    family: 'Roboto, sans-serif',
    weight: 'bold'
  },
  bodyFont: {
    size: 13,
    family: 'Roboto, sans-serif'
  },
  padding: 12,
  cornerRadius: 6,
  displayColors: false,
  callbacks: {
    title: function(tooltipItems) {
      if (tooltipItems.length > 0) {
        const dateLabel = tooltipItems[0].label;
        if (dateLabel) {
          try {
            const dateObj = new Date(dateLabel);
            if (!isNaN(dateObj.getTime())) {
              return formatChartTooltipTime(dateObj);
            }
          } catch (e) {
            console.warn('Ошибка при форматировании времени:', e);
          }
        }
        return tooltipItems[0].label;
      }
      return '';
    },
    label: function(context) {
      const formattedValue = formatTooltipLabel(context.parsed.y);
      const label = context.dataset.label || '';
      return `${label}: ${formattedValue}`;
    },
    afterLabel: function() {
      const reportTypeLabel = reportType || 'Н/Д';
      return [
        `Тип отчета: ${reportTypeLabel}`
      ];
    }
  }
});

/**
 * Глубоко объединяет объекты опций графика
 * @param {Object} target - Базовые опции
 * @param {Object} source - Пользовательские опции для слияния
 * @returns {Object} Объединенные опции
 */
export const mergeChartOptions = (target, source) => {
  const result = { ...target };
  
  for (const key in source) {
    if (Object.prototype.hasOwnProperty.call(source, key)) {
      if (source[key] && typeof source[key] === 'object' && !Array.isArray(source[key])) {
        // Рекурсивно объединяем вложенные объекты
        result[key] = mergeChartOptions(result[key] || {}, source[key]);
      } else {
        // Заменяем примитивные значения и массивы
        result[key] = source[key];
      }
    }
  }
  
  return result;
};

// Конфигурация опций графика
export const createChartOptions = (
  yAxisLabel,
  formatYAxisLabel,
  formatXAxisLabel,
  customTooltip,
  containerId,
  handleSelection,
  notifySelectionChanged
) => ({
  responsive: true,
  maintainAspectRatio: false,
  devicePixelRatio: window.devicePixelRatio || 1,
  elements: {
    point: {
      radius: 2,
      hitRadius: 10,
      hoverRadius: 5,
    },
    line: {
      tension: 0,
      borderWidth: 2,
    }
  },
  layout: {
    padding: {
      top: 10,
      right: 10,
      bottom: 10,
      left: 10
    },
    autoPadding: true
  },
  plugins: {
    legend: {
      position: 'top',
      labels: {
        font: {
          size: 12,
          family: 'Roboto, sans-serif'
        },
        usePointStyle: true,
        padding: 15
      }
    },
    title: {
      display: false,
      font: {
        size: 16,
        family: 'Roboto, sans-serif',
        weight: 'bold'
      },
      padding: {
        top: 5,
        bottom: 5
      }
    },
    tooltip: customTooltip,
    zoom: {
      limits: {
        x: {min: 'original', max: 'original', minRange: 10},
        y: {min: 'original', max: 'original'}
      },
      pan: {
        enabled: true,
        mode: 'x',
        modifierKey: 'shift',
        threshold: 10,
        onPan: function(context) {
          const chartSyncManager = window.chartSyncManager;
          if (chartSyncManager && chartSyncManager.syncEnabled && !chartSyncManager.suppressEvents) {
            const chart = context.chart;
            const xAxis = chart.scales.x;
            const range = {
              min: xAxis.min,
              max: xAxis.max
            };
            chartSyncManager.syncPan(containerId, range);
          }
        }
      },
      zoom: {
        wheel: {
          enabled: true,
          modifierKey: null,
          speed: 0.5,
        },
        pinch: {
          enabled: true
        },
        drag: {
          enabled: true,
          backgroundColor: 'rgba(75, 192, 192, 0.2)',
          borderColor: 'rgb(75, 192, 192)',
          borderWidth: 1
        },
        mode: 'x',
        onZoom: function(context) {
          const chartSyncManager = window.chartSyncManager;
          if (chartSyncManager && chartSyncManager.syncEnabled && !chartSyncManager.suppressEvents) {
            const chart = context.chart;
            const xAxis = chart.scales.x;
            const range = {
              min: xAxis.min,
              max: xAxis.max
            };

            if (chart.data && chart.data.labels && chart.data.labels.length > 0) {
              const minIndex = Math.floor(xAxis.min);
              const maxIndex = Math.ceil(xAxis.max);
              
              if (minIndex >= 0 && maxIndex < chart.data.labels.length) {
                const minLabel = chart.data.labels[minIndex];
                const maxLabel = chart.data.labels[maxIndex];
                
                if (minLabel instanceof Date && maxLabel instanceof Date) {
                  const selectionData = {
                    startDate: minLabel,
                    endDate: maxLabel,
                    startIndex: minIndex,
                    endIndex: maxIndex
                  };
                  
                  if (notifySelectionChanged) {
                    notifySelectionChanged(selectionData);
                  }
                }
              }
            }
            
            chartSyncManager.syncZoom(containerId, range);
          }
        }
      },
      resetSpeed: 0
    }
  },
  scales: {
    x: {
      ticks: {
        maxRotation: 0,
        minRotation: 0,
        autoSkip: true,
        maxTicksLimit: 12,
        font: {
          size: 12,
        },
        padding: 5,
        callback: function(value) {
          const label = formatXAxisLabel(this.getLabelForValue(value));
          if (typeof label === 'string' && label.includes('\n')) {
            return label.split('\n');
          }
          return label;
        }
      },
      grid: {
        color: 'rgba(0, 0, 0, 0.05)'
      },
      adapters: {
        date: {
          locale: 'ru',
          zone: 'Asia/Almaty'
        }
      },
      title: {
        display: false,
      }
    },
    y: {
      title: {
        display: true,
        text: yAxisLabel,
        font: {
          size: 12,
          family: 'Roboto, sans-serif'
        }
      },
      ticks: {
        font: {
          size: 12,
        },
        callback: function(value) {
          return formatYAxisLabel(value);
        }
      },
      grid: {
        color: 'rgba(0, 0, 0, 0.05)'
      },
      beginAtZero: true,
      grace: '10%'
    }
  },
  interaction: {
    mode: 'index',
    intersect: false
  },
  onClick: function(event, elements) {
    if (elements && elements.length > 0) {
      const element = elements[0];
      const pointIndex = element.index;
      const datasetIndex = element.datasetIndex;
      
      const chart = this;
      const label = chart.data.labels[pointIndex];
      const value = chart.data.datasets[datasetIndex].data[pointIndex];
      
      console.log(`ChartConfig: Клик по точке индекс=${pointIndex}, значение=${value}`, {
        label,
        datasetIndex,
        containerId
      });
      
      // Уведомляем о выделении точки
      if (handleSelection) {
        handleSelection(pointIndex, pointIndex);
      }
      
      // Синхронизируем выделение точки с другими графиками
      const chartSyncManager = window.chartSyncManager;
      if (chartSyncManager) {
        const timestamp = label instanceof Date ? label.getTime() : Date.now();
        
        chartSyncManager.notifyPointSelected({
          sourceContainerId: containerId,
          timestamp,
          pointIndex,
          datasetIndex,
          label,
          value,
          reportType: 'chart'
        }, containerId);
      }
    }
  },
  animation: {
    duration: 300
  }
}); 